import React from 'react';
import { KTMScreen, ActionEvent, RenderEvent, renderScreen, Dialog } from 'ktm-ui';
import { KTMApi } from 'api/KTMApi';
import { Layout, Button, Column, Panel, Display, Line, Card } from 'ktm-ui';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import { Button as BsButton, Modal } from 'react-bootstrap';
import "../../styles/Employee.css";
import InfoBox from '../../components/InfoBox';

interface IdsDateRange {
  Id: string;
  Policy_Id: string;
  Employee_Id: string;
  Dependent_Id: string | null;
  RangeType: string;
  FromDate: string | null;
  ToDate: string | null;
  Comment: string;
  IncludePartialAccrualCycle: boolean;
}

interface State {
  hasAccrual: boolean;
  isExactCycleMatch: boolean;
  fromDate: Date | null;
  toDate: Date | null;
  includePartialAccrualCycle: boolean;
  showConfirmation: boolean;
  screenToSubmit: any | null;
  pickerPos: {
    FromDate: { top: boolean; alignRight: boolean };
    ToDate: { top: boolean; alignRight: boolean };
  };
}

export default KTMScreen({ name: "planadmin.employee.edit-date-range" },
  class extends React.Component<any, any> {
    data: { dsDateRange: IdsDateRange };

    state: State = {
      hasAccrual: false,
      isExactCycleMatch: true,
      fromDate: null,
      toDate: null,
      includePartialAccrualCycle: true,
      showConfirmation: false,
      screenToSubmit: null,
      pickerPos: {
        FromDate: { top: false, alignRight: false },
        ToDate: { top: false, alignRight: false },
      },
    };

    private fromPickerRef = React.createRef<HTMLDivElement>();
    private toPickerRef = React.createRef<HTMLDivElement>();

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsDateRange";

      await KTMApi.start(screen, {
        "@dateRange_id": this.props.dateRange_id,
      });

      const { Employee_Id, FromDate, ToDate, IncludePartialAccrualCycle } = this.data.dsDateRange;

      this.setState({
        fromDate: FromDate ? moment(FromDate, "YYYY-MM-DD").toDate() : null,
        toDate: ToDate ? moment(ToDate, "YYYY-MM-DD").toDate() : null,
        includePartialAccrualCycle: IncludePartialAccrualCycle ?? true,
      });

      await this.checkExactCycleMatch(FromDate, ToDate);

      try {
        const response = await fetch(`/api/Employees/checkemployeehasaccrual?employeeId=${Employee_Id}`);
        const result = await response.json();
        this.setState({ hasAccrual: result.hasAccrual });
      } catch (err) {
        console.error("Failed to load HasAccrual status:", err);
      }

      screen.title = "Edit Date Range";
    }

    async checkExactCycleMatch(fromDate: string | null, toDate: string | null) {
      if (!fromDate) {
        this.setState({ isExactCycleMatch: true });
        return;
      }

      const query = new URLSearchParams({
        employeeId: this.data.dsDateRange.Employee_Id,
        fromDate,
        ...(toDate ? { toDate } : {}),
      });

      try {
        const response = await fetch(`/api/Employees/checkDatesMatchCycleStartAndEnd?${query}`);
        const result = await response.json();
        this.setState({ isExactCycleMatch: result.isExactCycleMatch });
      } catch (err) {
        console.error("Failed to check cycle match:", err);
      }
    }

    handleDateChange = async (value: Date | null, fieldName: "FromDate" | "ToDate") => {
      if (!value) {
        this.data.dsDateRange[fieldName] = null;
        this.setState({ [fieldName === "FromDate" ? "fromDate" : "toDate"]: null } as any);
        const { FromDate, ToDate } = this.data.dsDateRange;
        await this.checkExactCycleMatch(FromDate, ToDate);
        return;
      }

      const dateStr = moment.utc(value).format("YYYY-MM-DD");
      this.data.dsDateRange[fieldName] = dateStr;
      this.setState({ [fieldName === "FromDate" ? "fromDate" : "toDate"]: value } as any);
      const { FromDate, ToDate } = this.data.dsDateRange;
      await this.checkExactCycleMatch(FromDate, ToDate);
    };

    handleCalendarOpen = (fieldName: "FromDate" | "ToDate") => {
      const ref = fieldName === "FromDate" ? this.fromPickerRef : this.toPickerRef;
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const row = el.closest('.RT-Line__content') as HTMLElement | null;
      const containerRect = row ? row.getBoundingClientRect() : document.body.getBoundingClientRect();

      const CAL_HEIGHT = 320;
      const CAL_WIDTH = Math.min(Math.max(rect.width, 260), 420);

      const top = (window.innerHeight - rect.bottom) < CAL_HEIGHT;
      const wouldOverflowRight = (rect.left + CAL_WIDTH) > containerRect.right;
      const alignRight = wouldOverflowRight;

      this.setState(prev => ({
        pickerPos: { ...prev.pickerPos, [fieldName]: { top, alignRight } },
      }));
    };

    main({ field, draw, action }: RenderEvent) {
      if (this.state.showConfirmation) {
        return draw(this.renderConfirmationModal);
      }

      return (
        <Layout>
          {draw(this.header)}
          {draw(this.main_tab)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header() {
      return <Dialog.Header label="Edit Date Range" />;
    }

    footer({ action }: RenderEvent) {
      const d = this.data.dsDateRange;

      const from = d.FromDate ? moment.utc(d.FromDate, "YYYY-MM-DD") : null;
      const to   = d.ToDate   ? moment.utc(d.ToDate,   "YYYY-MM-DD") : null;

      const invalidDateRange = !!(from && to && !to.isAfter(from));

      const isDisabled =
        (!d.RangeType || d.RangeType === '-- Select --') ||
        (d.Dependent_Id === '-- Select --') ||
        !d.FromDate ||
        invalidDateRange;

      return (
        <Dialog.Footer>
          {isDisabled
            ? <button disabled className="btn btn-secondary">Save</button>
            : <Button action={action(this.submit)} label="Save" size="large" style="primary" />
          }
        </Dialog.Footer>
      );
    }

    main_tab({ draw }: RenderEvent) {
      const { hasAccrual, isExactCycleMatch } = this.state;
      const { RangeType, Dependent_Id } = this.data.dsDateRange;

      const isSelfCoverage = Dependent_Id === null || Dependent_Id === undefined;
      const isInactive = RangeType === "INACTIVE";

      const showInactiveEmployeeWarning =
        hasAccrual && isInactive && isSelfCoverage;

      return (
        <Panel>
          <Column>
            {showInactiveEmployeeWarning && draw(this.renderInactiveEmployeeDateRangeWarning)}
            {draw(this.details)}
          </Column>
        </Panel>
      );
    }

    details({ field }: RenderEvent) {
      const CustomClearIcon = () => <span style={{ cursor: 'pointer', padding: '0 5px' }}>❌</span>;

      const { hasAccrual, isExactCycleMatch, fromDate, toDate, includePartialAccrualCycle } = this.state;
      const { RangeType, Dependent_Id } = this.data.dsDateRange;

      const isSelfCoverage = Dependent_Id === null || Dependent_Id === undefined;
      const isInactive = RangeType === "INACTIVE";

      const showPartialCycleCheckbox =
        hasAccrual && !isExactCycleMatch && isInactive && isSelfCoverage;

      const invalidDateRange =
        fromDate && toDate && !moment.utc(toDate).isAfter(moment.utc(fromDate));

      return (
        <Card>
          <Line>
            <Display required field={field("RangeType")} />
            <Display required field={field("Dependent_Id")} />
          </Line>

          <Line>
            <div
              ref={this.fromPickerRef}
              className={[
                "date-picker-container",
                this.state.pickerPos.FromDate.top ? "picker--top" : "",
                this.state.pickerPos.FromDate.alignRight ? "picker--align-right" : "",
              ].join(" ")}
              style={{ width: "100%" }}
            >
              <label className="RT-Section-Header">
                <span className="text-danger">* </span>From Date
              </label>
              <DatePicker
                onChange={(date) => this.handleDateChange(date, "FromDate")}
                value={fromDate}
                format="y-MM-dd"
                clearIcon={<CustomClearIcon />}
                dayPlaceholder="DD"
                monthPlaceholder="MM"
                yearPlaceholder="YYYY"
                onCalendarOpen={() => this.handleCalendarOpen("FromDate")}
              />
            </div>

            <div
              ref={this.toPickerRef}
              className={[
                "date-picker-container",
                this.state.pickerPos.ToDate.top ? "picker--top" : "",
                this.state.pickerPos.ToDate.alignRight ? "picker--align-right" : "",
              ].join(" ")}
              style={{ width: "100%" }}
            >
              <label className="RT-Section-Header">To Date</label>
              <DatePicker
                onChange={(date) => this.handleDateChange(date, "ToDate")}
                value={toDate}
                format="y-MM-dd"
                clearIcon={<CustomClearIcon />}
                dayPlaceholder="DD"
                monthPlaceholder="MM"
                yearPlaceholder="YYYY"
                onCalendarOpen={() => this.handleCalendarOpen("ToDate")}
              />
              {invalidDateRange && (
                <div style={{ color: "red", marginTop: "5px" }}>
                  To Date must be later than From Date.
                </div>
              )}
            </div>
          </Line>

          <Display field={field("Comment")} />

          {/* If you re-enable partial-cycle option later:
          {showPartialCycleCheckbox && (
            <Line>
              <InfoBox>
                {this.renderInactiveEmployeeMidCycleWarning()}
                <label style={{ display: "flex", alignItems: "center", justifyContent: "end", marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={includePartialAccrualCycle}
                    onChange={(e) => this.setState({ includePartialAccrualCycle: e.target.checked })}
                    style={{ marginRight: 10, width: 20, height: 20, cursor: "pointer" }}
                  />
                  <span style={{ fontWeight: "bold" }}>{includePartialAccrualCycle ? "Yes" : "No"}</span>
                </label>
              </InfoBox>
            </Line>
          )} */}
        </Card>
      );
    }

    renderConfirmationModal() {
      return (
        <Modal
          className="react-modal sc-backdated-date-range-confirmation"
          show={this.state.showConfirmation}
          backdrop="static"
        >
          <Modal.Header>
            <Modal.Title>Backdated Date Range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Card>
              <p>
                Date range is backdated. The employee will not be able to claim expenses dated on or after this start date, but credit amounts the
                employee has already earned to the current date will remain eligible in their account and credit schedule.
              </p>
              <p>
                If backdating within a previous benefit year, the previous year’s benefit totals will not be affected.
              </p>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <BsButton variant="secondary" onClick={() => this.setState({ showConfirmation: false, screenToSubmit: null })}>
              Cancel
            </BsButton>
            <BsButton
              variant="primary"
              onClick={() => {
                this.setState({ showConfirmation: false }, async () => {
                  await this.handleConfirmedSubmit();
                });
              }}
            >
              Continue
            </BsButton>
          </Modal.Footer>
        </Modal>
      );
    }

    renderInactiveEmployeeDateRangeWarning() {
      return (
        <InfoBox>
          <p>Applying an Inactive Date Range to the Employee will make the employee:</p>
          <ul style={{ marginLeft: 20, listStyle: "disc", paddingLeft: 20 }}>
            <li>unable to claim expenses dated within and including the dates entered.</li>
            <li>ineligible for Scheduled Credit increment amounts where they are fully inactive.</li>
          </ul>
          <p>The employee’s yearly benefit total will reflect the update immediately. Start and end dates are inclusive.</p>
        </InfoBox>
      );
    }

    renderInactiveEmployeeMidCycleWarning() {
      return (
        <>
          <p>
           The selected start and/or end dates occur mid-increment. Would you like to <strong>include the full amount of the partially eligible increment(s)</strong> as a part of this employee’s 
           earned yearly credit total? Fully ineligible increments will remain excluded.
          </p>
          <p>
            Unchecking this option will result in those partially eligible increments being excluded in full from the employee’s credit total for the year.
          </p>
        </>
      );
    }

    async submit({ screen }: ActionEvent) {
      const fromDate = moment.utc(this.data.dsDateRange.FromDate!, "YYYY-MM-DD");
      const today = moment.utc().startOf("day");

      const isInactive = this.data.dsDateRange.RangeType === "INACTIVE";
      const isSelfCoverage = this.data.dsDateRange.Dependent_Id === null || this.data.dsDateRange.Dependent_Id === undefined;
      const isBackdated = fromDate.isBefore(today);

      if (isInactive && isSelfCoverage && isBackdated && this.state.hasAccrual) {
        this.setState({ showConfirmation: true, screenToSubmit: screen });
        return;
      }

      await this.handleConfirmedSubmit(screen);
    }

    handleConfirmedSubmit = async (screenFromSubmit?: any) => {
      const screen = screenFromSubmit || this.state.screenToSubmit;
      if (!screen) { console.error("Submission failed: screen context was lost."); return; }

      this.data.dsDateRange.IncludePartialAccrualCycle = this.state.includePartialAccrualCycle;

      const logPayload = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          EmployeeId: this.data.dsDateRange.Employee_Id,
          DependentId: this.data.dsDateRange.Dependent_Id,
          RangeType: this.data.dsDateRange.RangeType,
          FromDate: this.data.dsDateRange.FromDate,
          ToDate: this.data.dsDateRange.ToDate,
          Comment: this.data.dsDateRange.Comment,
          IncludePartialCycle: this.data.dsDateRange.IncludePartialAccrualCycle,
          Action: "Date Range Edited",
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dateRange_id": this.props.dateRange_id,
        "@dsDateRange.Dependent_Id": this.data.dsDateRange.Dependent_Id,
        "@dsDateRange.RangeType": this.data.dsDateRange.RangeType,
        "@dsDateRange.FromDate": this.data.dsDateRange.FromDate,
        "@dsDateRange.ToDate": this.data.dsDateRange.ToDate,
        "@dsDateRange.Comment": this.data.dsDateRange.Comment,
        "@dsDateRange.IncludePartialAccrualCycle": this.data.dsDateRange.IncludePartialAccrualCycle,
      }).then(async () => {
        await fetch("/api/PlanAdmin/adddaterangelogs", logPayload);
      });

      screen.close({ continue: true });
    };
  }
);
